import { Grid, Typography } from '@mui/material';
import { ApprovalButton } from '@tassat/button';
import {
    ClientMessagesStatusEnum,
    ClientMessagesStatusMap,
    ClientMessagesTypeMap
} from '@tassat/common-definitions';
import moment from 'moment';
import React from 'react';

import { JS_TIMER_MAX } from 'Lib/constant';
import { formatEpochDate } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole, getUser } from 'Selectors/common';
import ClientMessageContentDetails from 'SharedComponents/clientMessages/clientMessageContentDetails';

import type { ClientMessageDetailsProps } from '.';

export function ClientMessageDetails(props: ClientMessageDetailsProps) {
    const {
        classes,
        selectedMessage,
        confirmApprove,
        confirmDecline
    } = props;

    const currentUser = useAppSelector(getUser);
    const userRole = useAppSelector(getCurrentRole);

    const [ showApproval, setShowApproval ] = React.useState(false);
    React.useEffect(() => {
        const remainingTime = selectedMessage?.EndAt ? moment(selectedMessage.EndAt, 'x')
            .valueOf() - moment()
            .valueOf() : 0;
        let approvalTimeout;

        if (remainingTime > 100) {
            setShowApproval(true);

            // If remaining time is too large for a timer, then don't worry about it as it will be multiple days
            if (remainingTime < JS_TIMER_MAX){
                approvalTimeout = setTimeout(() => {
                    setShowApproval(false);
                }, remainingTime - 100);
            }
        }

        return () => {
            clearTimeout(approvalTimeout);
        };
    }, [selectedMessage.EndAt]);

    const showApprovalButtons = React.useMemo(() => {
        const userCanApprove = userRole.IsApprover &&
            ((selectedMessage.CreatedBy !== currentUser.Email && !selectedMessage.ModifiedBy) ||
                (selectedMessage.ModifiedBy && selectedMessage.ModifiedBy !== currentUser.Email));

        return selectedMessage.Status === ClientMessagesStatusEnum.PENDING && userCanApprove;
    }, [ selectedMessage, currentUser, userRole ]);

    const showApproveButton = React.useMemo(() => {
        const now = moment();
        const endDate = moment(selectedMessage.EndAt, 'x');

        return showApproval && now.valueOf() <= endDate.valueOf();
    }, [ selectedMessage.Status, selectedMessage.EndAt, showApproval ]);

    return (
        <>
            <Grid
                className={classes.headerContainer}
                container
                direction='row'
                spacing={0}
            >
                <Typography
                    className={classes.headerText}
                    variant='h4'
                >
                    <strong>MESSAGE DETAILS</strong>
                </Typography>
                <Typography
                    className={classes.headerText}
                    variant='h4'
                >
                    |
                </Typography>
                <Typography
                    className={classes.headerText}
                    id='clientMessageDetails_createdTime_value'
                    variant='h4'
                >
                    <strong>Created:</strong> {formatEpochDate(selectedMessage?.CreatedAt)}
                </Typography>
                <Typography
                    className={classes.headerText}
                    id='clientMessageDetails_createdBy_value'
                    variant='h4'
                >
                    <strong>By:</strong> {selectedMessage?.CreatedByUsername ?? selectedMessage?.CreatedBy}
                </Typography>
                {showApprovalButtons &&
                    <Grid
                        className={classes.approvalButtonContainer}
                        container
                        direction='row'
                        item
                        justifyContent='flex-end'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <ApprovalButton
                            id='clientMessageDetails_declineMessage_button'
                            onClick={confirmDecline}
                            type='decline'
                        />
                        {showApproveButton &&
                            <ApprovalButton
                                id='clientMessageDetails_approveMessage_button'
                                onClick={confirmApprove}
                                type='approve'
                            />}
                    </Grid>}
            </Grid>
            <Grid
                className={classes.headerDetailsContainer}
                container
                direction='row'
                spacing={0}
            >
                <Grid
                    className={classes.headerSmallDetail}
                    container
                    direction='row'
                    item
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.headerLabel}
                        id='clientMessageDetails_messageType_label'
                        variant='body2'
                    >Type:
                    </Typography>
                    <Typography
                        className={classes.headerAttribute}
                        id='clientMessageDetails_messageType_value'
                        variant='body1'
                    >{ClientMessagesTypeMap[selectedMessage?.ClientMsgType] || '-'}
                    </Typography>
                </Grid>
                <Grid
                    className={classes.headerSmallDetail}
                    container
                    direction='row'
                    item
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.headerLabel}
                        id='clientMessageDetails_messageStatus_label'
                        variant='body2'
                    >Status:
                    </Typography>
                    <Typography
                        className={classes.headerAttribute}
                        id='clientMessageDetails_messageStatus_value'
                        variant='body1'
                    >{ClientMessagesStatusMap[selectedMessage?.Status] || '-'}
                    </Typography>
                </Grid>
            </Grid>
            <ClientMessageContentDetails
                selectedMessage={selectedMessage}
            />
        </>
    );
}